import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SeatMapOverview,
  SelectedTariff as SeatMapSelectedTariff,
  SeatAllocation,
  SeatMapEventType,
  AllocationChangePayload,
  SeatChangePayload,
  AreaChangePayload,
  AllocationStatus,
} from '@planne-software/seat-map';
import { SeatMapSlotResource } from '@src/core/buyerApiClient';
import { useAppSelector, useAppDispatch } from '@src/core/hooks';
import {
  currentProductAdderStatus,
  loadSeatMap,
  pricingTariffSelectionsHashSelector,
  schedulingSeatMapLoadingSelector,
  schedulingSeatMapSelector,
} from '@src/stores/product';
import useTranslate from '@src/i18n/useTranslate';
import { getAllocationsExtrasCents, transformSeatMapForUI } from '@src/common/seatMaps';
import { useWidgetId } from '@src/core/IdProvider';
import type { EmbedPlanneSeatmapProps, UseEmbedPlanneSeatmapResult } from './types';

export function useEmbedPlanneSeatmap(props: Readonly<EmbedPlanneSeatmapProps>): UseEmbedPlanneSeatmapResult {
  const { scheduling, timeMinutes, onSelectionChange, selectedTariffs, embedActions } = props;

  const { locale, i18n } = useTranslate();
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const selections = useAppSelector(pricingTariffSelectionsHashSelector(widgetId));

  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling.id, timeMinutes?.minutes, selections));
  const loadingSeatMap = useAppSelector(
    schedulingSeatMapLoadingSelector(scheduling.id, timeMinutes?.minutes, selections),
  );

  const [allocationFromMap, setAllocationFromMap] = useState<SeatAllocation[]>([]);
  const [allocationStatus, setAllocationStatus] = useState<AllocationStatus>(AllocationStatus.INCOMPLETE);

  const transformedSeatMap = useMemo(() => (seatMap ? transformSeatMapForUI(seatMap) : undefined), [seatMap]);

  const status = useAppSelector(currentProductAdderStatus(widgetId));

  const { onDismiss, onConfirm, primaryLabel } = embedActions;

  const handleGoBack = useCallback(() => onDismiss(), [onDismiss]);

  const handleGoForwards = useCallback(() => onConfirm(), [onConfirm]);

  const defaultPrimaryLabel = i18n('adder_goForwardsFooter');
  const primaryButtonText = useMemo(() => primaryLabel ?? defaultPrimaryLabel, [primaryLabel, defaultPrimaryLabel]);

  const seatMapSelectedTariffs = useMemo(
    () => selectedTariffs.map(({ tariff, quantity }) => ({ tariff, quantity })) as unknown as SeatMapSelectedTariff[],
    [selectedTariffs],
  );

  const isSelectionComplete = useMemo(() => allocationStatus === AllocationStatus.VALID, [allocationStatus]);

  const disableButton = useMemo(
    () => status === 'adding_to_cart' || !isSelectionComplete,
    [status, isSelectionComplete],
  );

  const totalExtrasCents = useMemo(() => getAllocationsExtrasCents(allocationFromMap), [allocationFromMap]);

  const handleSeatMapEvent = useCallback(
    (eventType: SeatMapEventType, payload: AllocationChangePayload | SeatChangePayload | AreaChangePayload) => {
      if (eventType === SeatMapEventType.ALLOCATION_CHANGE) {
        const allocationPayload = payload as AllocationChangePayload;
        setAllocationFromMap(allocationPayload.seatAllocations);
        setAllocationStatus(allocationPayload.status);
      }
    },
    [],
  );

  const selectedResourcesForParent = useMemo(
    () =>
      allocationFromMap.map(({ seat: { resource }, tariffs }) => ({
        resourceSlot: resource as unknown as SeatMapSlotResource,
        tariffs: tariffs.map((tariff: { id: string }) => ({
          tariffId: tariff.id,
          quantity: 1,
        })),
      })),
    [allocationFromMap],
  );

  const hasNotifiedInitialState = useRef(false);

  useEffect(() => {
    if (!hasNotifiedInitialState.current) {
      hasNotifiedInitialState.current = true;
      return;
    }

    onSelectionChange(selectedResourcesForParent, isSelectionComplete, totalExtrasCents);
  }, [isSelectionComplete, totalExtrasCents, selectedResourcesForParent, onSelectionChange]);

  useEffect(() => {
    dispatch(loadSeatMap(scheduling.id, timeMinutes?.minutes, selections));
  }, [dispatch, scheduling.id, timeMinutes?.minutes, selections]);

  if (loadingSeatMap || (seatMap && !transformedSeatMap)) {
    return { view: 'skeleton' };
  }

  if (!transformedSeatMap || !seatMap) {
    return { view: 'empty' };
  }

  return {
    view: 'ready',
    locale,
    loadingSeatMap: Boolean(loadingSeatMap),
    seatMapOverview: {
      name: 'Seat Map',
      id: transformedSeatMap.id,
      areas: transformedSeatMap.areas as unknown as SeatMapOverview['areas'],
      categories: seatMap.categories as unknown as SeatMapOverview['categories'],
    } as SeatMapOverview,
    onEvent: handleSeatMapEvent,
    seatMapSelectedTariffs,
    actions: {
      primaryButton: {
        disabled: disableButton,
        onClick: handleGoForwards,
        children: primaryButtonText,
      },
      secondaryButton: {
        onClick: handleGoBack,
      },
    },
  };
}
